import { useEffect, useState } from 'react';
import { Grid, DialogContent, DialogTitle, CircularProgress, Typography, Button, Chip } from '@mui/material';
import { RoundedDialog, StandardDialogActions, SelectableButton } from '../Components';
import { AddLinkRounded, CloseRounded } from '@mui/icons-material';
import { isEmpty, isArray, isFunction } from 'lodash';
import SourceSelector from './SourceSelector';
import StateManager from '../StateManager';
import LinkItem from './LinkItem';
import axios from 'axios';

export default function LinksDialog({ open, onClose, entryType, entryId, onResult }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open || !entryId) return;

    getLinks();
  }, [entryId, open]); // eslint-disable-line

  function getLinks() {
    setLoading(true);

    axios
      .get('/links/getLinksForEntry', { params: { entryId } })
      .then(({ data }) => setLinks(data))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setLoading(false));
  }

  function createLink(params) {
    setSaving(true);

    axios
      .post('/links/saveLink', { fromType: entryType, from: entryId, ...params })
      .then(({ data }) => {
        isFunction(onResult) && onResult(data);
        onClose();
        StateManager.setSuccessAlert('Link has been added');
      })
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => {
        setSaving(false);
        setAdding(false);
      });
  }

  function confirmLinkDelete(linkId) {
    StateManager.setConfirm('You are about to delete this link', () => deleteLink(linkId));
  }

  function deleteLink(linkId) {
    axios
      .post('/links/deleteLink', { linkId })
      .then(() => {
        StateManager.setSuccessAlert('Link has been deleted');
        isFunction(onResult) && onResult();
        setLinks(links.filter((x) => x._id !== linkId));
      })
      .catch((err) => StateManager.setAxiosErrorAlert(err));
  }

  const hasLinks = isEmpty(links) || !isArray(links);

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="md" fullWidth className="scroll-bar">
      <DialogTitle>These are the links for this entry</DialogTitle>

      <DialogContent>
        {loading ? (
          <Grid container alignItems={'center'} justifyContent={'center'} sx={{ height: 150 }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {hasLinks ? (
              <Grid container sx={{ py: 2 }}>
                <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>No links are found</Typography>
              </Grid>
            ) : (
              <Grid container>
                {links.map((item) => (
                  <LinkItem key={item._id} data={item} entryId={entryId} onDelete={() => confirmLinkDelete(item._id)} />
                ))}
              </Grid>
            )}
            {adding && (
              <Grid container sx={{ mt: 1 }}>
                <SourceSelector
                  onResult={createLink}
                  cancelButton={
                    <Button color="error" onClick={() => setAdding(false)} startIcon={<CloseRounded />}>
                      cancel
                    </Button>
                  }
                />
              </Grid>
            )}
            <Grid container sx={{ mt: 3 }}>
              {saving ? (
                <CircularProgress />
              ) : (
                <>
                  {!adding && (
                    <Button variant="contained" onClick={() => setAdding(true)} startIcon={<AddLinkRounded />}>
                      Add link
                    </Button>
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </DialogContent>

      <StandardDialogActions onClose={onClose} onDone={onClose} />
    </RoundedDialog>
  );
}
